/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { configureStore } from '@reduxjs/toolkit';
import { Sentry } from 'lib-shared-sentry';
import createSagaMiddleware from 'redux-saga';
import { AllEffect } from 'redux-saga/effects';

import { api } from 'app/app.api';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { actions as conversationsActions } from 'components/conversation/actions/conversations';
import { notificationActions } from 'components/notifications/actions/notification';
import { createSyncMiddleware } from 'middleware/sync';
import { createReducer } from 'reducer';

import { elevioApiMiddleware } from './assets/components/conversation/components/integrations/elevio/src/utils/apis/elevioApi';

/** Creates a redux saga middleware. Make sure this is last in the middleware. */
const sagaMiddleware = createSagaMiddleware({
  // Saga's don't propagate errors.
  onError: (error, { sagaStack }) => {
    Sentry.captureException(error, { extra: { sagaStack } });
  },
});

/** Creates the sync middleware. */
const syncMiddleware = createSyncMiddleware({
  prepareState: (state) => ({
    conversations: state.conversations,
  }),
  actionPredicate: (action) =>
    [
      notificationActions.notificationRemoveByType.toString(),
      conversationActions.conversationAddSubmittedMessage.toString(),
      conversationActions.conversationSubmitMessageSucceeded.toString(),
      conversationActions.conversationSubmitMessageFailed.toString(),
      conversationsActions.conversationsIncreasingUnread.toString(),
      conversationsActions.conversationsResetUnread.toString(),
      conversationsActions.conversationsAdd.toString(),
      conversationsActions.conversationsRemove.toString(),
      conversationsActions.conversationsUpdateUser.toString(),
      conversationsActions.conversationsUnassignSelf.toString(),
      conversationsActions.conversationsSaveTempMessage.toString(),
      conversationsActions.conversationsSaveTempWrapup.toString(),
      conversationsActions.conversationsCloseConversation.toString(),
      conversationsActions.conversationsConversationToPending.toString(),
      conversationsActions.conversationsConversationCancelPending.toString(),
      conversationsActions.conversationsConversationOfferAccepted.toString(),
      conversationsActions.conversationsConversationClaimed.toString(),
      conversationsActions.conversationsConversationTransfered.toString(),
      conversationsActions.conversationsConversationAssistedTransferAccepted.toString(),
    ].includes(action.type),
});

/** The enhanced store and subscribe router in debug env ie. for devtools. */
export function setupStore(
  sagas: () => Generator<AllEffect<unknown>>,
  middlewares: any[],
) {
  const store = configureStore({
    reducer: createReducer(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // Temporarily disabled until everything has been converted to
        // redux-toolkit.
        serializableCheck: false,
        // Disabled as it slows down development build.
        immutableCheck: false,
      }).concat(
        api.middleware,
        ...elevioApiMiddleware,
        ...middlewares,
        syncMiddleware,
        sagaMiddleware,
      ),
  });

  sagaMiddleware.run(sagas);

  return store;
}
